import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ParseKeys } from "i18next";
import {
  resolveAccessCode,
  getCreditor,
  updateCreditor,
  type Bank,
  type Creditor,
  type CreditorType,
} from "./api/client";
import { ContractTerms } from "./components/ContractTerms";
import { FieldError, inputBorderClass } from "./components/FieldError";
import { LanguageSwitcher } from "./components/LanguageSwitcher";
import logoImg from "./assets/Eversheds-Sutherland-DarkBlue-300x300-rgb.jpg";
import { KONKURSSIPAIVA } from "./constants";
import {
  DEFAULT_PI_INTEREST_RATE,
  formatIban,
  parseInterestRate,
  roundInterestRate,
  toBackendAmount,
  toFinnishAmount,
  toFinnishDate,
  toFinnishFloat,
  toIsoDate,
  validateEmail,
  validateFinnishAmount,
  validateFinnishDate,
  validateIban,
  validateInterestRate,
  validateOptional,
  validatePhone,
  validateRequired,
  validateText,
} from "./validation";

type Step = "auth" | "edit" | "confirm" | "success" | "approved";

// Creditor:type options per api-spec.yaml. The customer selects exactly one;
// the chosen value is submitted as Creditor.type in PUT /creditor/{id}.
// labelKey is the option text in src/i18n/locales.
const CREDITOR_TYPE_OPTIONS: { value: CreditorType; labelKey: ParseKeys }[] = [
  { value: "2.1", labelKey: "creditorType.2_1" },
  { value: "2.2", labelKey: "creditorType.2_2" },
  { value: "2.3", labelKey: "creditorType.2_3" },
  { value: "2.4", labelKey: "creditorType.2_4" },
];

// participate_decision_making options: true = 1, false = 0 on the wire.
// Shared by the edit form radio group and the summary.
const PARTICIPATE_OPTIONS: { value: boolean; labelKey: ParseKeys }[] = [
  { value: false, labelKey: "participate.no" },
  { value: true, labelKey: "participate.yes" },
];

// An empty or unknown server-side type selects no option ("" = not
// chosen yet); the customer must choose one before continuing.
const normalizeCreditorType = (type: string | undefined): CreditorType =>
  CREDITOR_TYPE_OPTIONS.find((o) => o.value === type)?.value ?? "";

function App() {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>("auth");
  const [authCode, setAuthCode] = useState("");
  // authError, saveError and fieldErrors hold translation keys ("" = no
  // error), so messages already shown follow a language switch.
  const [authError, setAuthError] = useState<ParseKeys | "">("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<ParseKeys | "">("");
  // Field validation errors keyed by input id ("" = valid), shown under
  // each input by <FieldError>. Fields show an error on blur when a value
  // is invalid; required fields left empty are flagged on submit.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Creditor ID from resolve endpoint (kept for PUT)
  const [creditorId, setCreditorId] = useState("");

  // Full creditor entity as returned by GET /creditor/{id}.
  // PUT /creditor/{id} replaces the whole entity, so type-specific fields
  // (type, penalty_interest, priority, bank, …) are echoed back untouched.
  const [originalCreditor, setOriginalCreditor] = useState<Creditor | null>(
    null,
  );

  const [noEmail, setNoEmail] = useState(false);
  const [noPhone, setNoPhone] = useState(false);

  // Creditor:type radio selection ("2.1"–"2.4" per api-spec.yaml;
  // "" = not chosen yet, nothing selected)
  const [creditorType, setCreditorType] = useState<CreditorType>("");

  // Claims map from API (keys like "MT-12345"), amounts are editable
  const [claims, setClaims] = useState<Record<string, string>>({});

  // Block 2.2 – penalty interest (PenaltyInterest per api-spec.yaml).
  // piClaimed = "Vaadin viivästyskorkoa" (penalty_interest.claimed); the
  // remaining fields are shown only when it is checked.
  // piAmount is a two-decimal string on the wire (e.g. "56.78"),
  // displayed/input in Finnish format ("56,78") via
  // toFinnishFloat()/toBackendAmount().
  // piStartingDate = penalty_interest.starting_date, held in the UI in
  // Finnish date format ("6.12.2026") via toFinnishDate()/toIsoDate();
  // the API value is ISO ("2026-12-06"). The input shares one grid row
  // with the amount field (side by side on lg+) for type 2.2 and type
  // 2.4 whenever "Vaadin
  // viivästyskorkoa" (piClaimed) is checked – independent of the
  // etuoikeutta checkbox.
  // piInterestRate is a float in UI state (9.5 = 9,50 %), shown in a
  // numeric input with up/down buttons stepping 0,1; null = empty
  // field. Sent to the API as a two-decimal string (e.g. "9.50").
  const [piClaimed, setPiClaimed] = useState(false);
  const [piAmount, setPiAmount] = useState("");
  const [piStartingDate, setPiStartingDate] = useState("");
  const [piInterestRate, setPiInterestRate] = useState<number | null>(
    DEFAULT_PI_INTEREST_RATE,
  );
  // piInterestRate as text for the shared validators ("" = empty)
  const piInterestRateText = piInterestRate === null ? "" : String(piInterestRate);
  const [piBasis, setPiBasis] = useState("");
  const [piClaimedAfterBankruptcy, setPiClaimedAfterBankruptcy] =
    useState(false);

  // Block 2.2 – priority claim (ClaimedBasis per api-spec.yaml)
  const [priorityClaimed, setPriorityClaimed] = useState(false);
  const [priorityBasis, setPriorityBasis] = useState("");

  // Block 2.2 – not_conditional_amount (ClaimedBasis per api-spec.yaml)
  const [notConditionalClaimed, setNotConditionalClaimed] = useState(false);
  const [notConditionalBasis, setNotConditionalBasis] = useState("");

  // Block 2.2 – Bank.bank_account_holder_name (IBAN edits bankAccount)
  const [bankHolderName, setBankHolderName] = useState("");

  // Blocks 2.2/2.3 – participate_decision_making (true = 1, false = 0).
  // Loaded from the server; when unset, "en osallistu" (false) is
  // preselected.
  const [participateDecisionMaking, setParticipateDecisionMaking] =
    useState(false);

  // Block 2.3 – material_delivery_email (Types 2.3, 2.4)
  const [materialDeliveryEmail, setMaterialDeliveryEmail] = useState("");

  // Editable customer data
  const [name, setName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [bankAccount, setBankAccount] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState("");

  const setFieldError = (id: string, message: string) =>
    setFieldErrors((prev) => ({ ...prev, [id]: message }));

  // aria attributes linking an input to its <FieldError>
  const ariaFieldError = (id: string) => ({
    "aria-invalid": Boolean(fieldErrors[id]),
    "aria-describedby": fieldErrors[id] ? `${id}-error` : undefined,
  });

  // Validates the fields visible for the selected type and checkboxes;
  // hidden fields are skipped. Returns the errors keyed by input id
  // ("" = valid) in form order, so the first invalid one can be focused.
  const validateVisibleFields = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    // Customer data (all types)
    errors.name = validateRequired(name, validateText);
    if (!noEmail) {
      errors.customerEmail = validateRequired(customerEmail, validateEmail);
    }
    if (!noPhone) {
      errors.phone = validateRequired(phone, validatePhone);
    }
    // The server requires an email or a phone, so both boxes can't be ticked
    if (noEmail && noPhone) {
      errors.noPhone = "validation.emailOrPhone";
    }
    // Creditor type radio group (keyed by its name)
    if (!creditorType) {
      errors.creditorType = "validation.chooseOne";
    }
    // Material delivery email (blocks 2.3 and 2.4)
    if (creditorType === "2.3" || creditorType === "2.4") {
      errors.materialDeliveryEmail = validateRequired(materialDeliveryEmail, validateEmail);
    }
    // Claim amounts (blocks 2.2 and 2.4)
    if (creditorType === "2.2" || creditorType === "2.4") {
      for (const [claimId, amount] of Object.entries(claims)) {
        errors[`claim:${claimId}`] = validateRequired(amount, validateFinnishAmount);
      }
    }
    const piVisible =
      (creditorType === "2.2" || creditorType === "2.4") && piClaimed;
    if (piVisible) {
      errors.piAmount = validateRequired(piAmount, validateFinnishAmount);
      errors.piStartingDate = validateRequired(piStartingDate, validateFinnishDate);
      errors.piInterestRate = validateRequired(piInterestRateText, validateInterestRate);
    }
    // Bank details (blocks 2.2–2.4): the server requires both values
    if (creditorType === "2.2" || creditorType === "2.3" || creditorType === "2.4") {
      errors.bankHolderName = validateRequired(bankHolderName, validateText);
      errors.blockBankAccount = validateRequired(bankAccount, validateIban);
    }
    return errors;
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError("");
    setIsLoading(true);

    try {
      const normalizedCode = authCode.replace(/\s/g, "").toUpperCase();

      // Step 1: resolve access code → creditor ID
      const id = await resolveAccessCode(normalizedCode);

      // Step 2: fetch creditor data
      const creditor = await getCreditor(id);

      setCreditorId(id);
      setOriginalCreditor(creditor);
      setCreditorType(normalizeCreditorType(creditor.type));
      setName(creditor.name);
      setCustomerEmail(creditor.email);
      setNoEmail(creditor.no_email ?? false);
      setNoPhone(creditor.no_phone ?? false);
      setPhone(creditor.phone ?? "");
      setBankAccount(creditor.bank?.bank_account ?? "");
      setBankHolderName(creditor.bank?.bank_account_holder_name ?? "");
      setMaterialDeliveryEmail(creditor.material_delivery_email ?? "");
      setAdditionalInfo(creditor.additional_info ?? "");
      // Block 2.2 fields
      const pi = creditor.penalty_interest;
      setPiClaimed(pi?.claimed ?? false);
      setPiAmount(toFinnishFloat(pi?.amount));
      setPiStartingDate(toFinnishDate(pi?.starting_date));
      setPiInterestRate(parseInterestRate(pi?.interest_rate) ?? DEFAULT_PI_INTEREST_RATE);
      setPiBasis(pi?.basis ?? "");
      setPiClaimedAfterBankruptcy(pi?.claimed_after_bankruptcy ?? false);
      setPriorityClaimed(creditor.priority?.claimed ?? false);
      setPriorityBasis(creditor.priority?.basis ?? "");
      setNotConditionalClaimed(
        creditor.not_conditional_amount?.claimed ?? false,
      );
      setNotConditionalBasis(creditor.not_conditional_amount?.basis ?? "");
      setParticipateDecisionMaking(
        creditor.participate_decision_making ?? false,
      );

      const claimsData = creditor.claims ?? {};
      // Convert backend dot-decimal amounts to Finnish comma format for display
      const finnishClaims: Record<string, string> = {};
      for (const [id, amount] of Object.entries(claimsData)) {
        finnishClaims[id] = toFinnishAmount(amount);
      }
      setClaims(finnishClaims);
      // Approved data can no longer be edited: show it read-only
      setStep(creditor.approved ? "approved" : "edit");
    } catch (err) {
      if (err instanceof Error && "status" in err) {
        const httpErr = err as { status: number };
        if (httpErr.status === 404) {
          setAuthError("errors.invalidCode");
        } else if (httpErr.status === 429) {
          setAuthError("errors.tooManyAttempts");
        } else {
          setAuthError("errors.server");
        }
      } else {
        setAuthError("errors.connection");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    setSaveError("");
    setIsSaving(true);

    try {
      // Convert Finnish amounts back to backend format before submission
      const backendClaims: Record<string, string> = {};
      for (const [id, amount] of Object.entries(claims)) {
        backendClaims[id] = toBackendAmount(amount);
      }

      // PUT /creditor/{id} replaces the whole entity, so we spread the
      // original creditor to preserve fields this form does not edit
      // (type, penalty_interest, priority, claims rules); type-specific
      // fields edited by the form are overridden below.
      const prev = originalCreditor ?? ({} as Creditor);

      // Types 2.2–2.4 require a bank object with both values; both are
      // required fields, validated before the confirm step.
      const bank: Bank | undefined = bankAccount
        ? {
            bank_account: bankAccount,
            bank_account_holder_name: bankHolderName,
          }
        : undefined;

      const creditor: Creditor = {
        ...prev,
        // Submit the radio selection as Creditor:type (api-spec.yaml enum)
        type: creditorType,
        name,
        email: noEmail ? "" : customerEmail.trim(),
        no_email: noEmail,
        phone: noPhone ? "" : phone.trim(),
        no_phone: noPhone,
        bank,
        // Block 2.2 – penalty interest (PenaltyInterest per api-spec.yaml).
        // `claimed` is the "Vaadin viivästyskorkoa" checkbox; when it is
        // not checked the remaining fields are sent as null.
        // amount and interest_rate are sent as two-decimal strings with
        // dot separator (e.g. "56.78", "9.50"), null when empty. Not
        // Finnish-formatted. A claimed basis may be "" but not null (the
        // server requires a string), here and in priority and
        // not_conditional_amount below.
        penalty_interest: piClaimed
          ? {
              claimed: true,
              amount: piAmount ? toBackendAmount(piAmount) : null,
              // starting_date (penalty_interest.starting_date) – shown for
              // types 2.2 and 2.4 when the "Vaadin viivästyskorkoa" checkbox
              // is checked (independent of the etuoikeutta checkbox). The UI
              // holds Finnish format ("6.12.2026"); convert back to ISO for
              // the API.
              starting_date: toIsoDate(piStartingDate),
              interest_rate:
                piInterestRate !== null ? piInterestRate.toFixed(2) : null,
              basis: piBasis,
              claimed_after_bankruptcy: piClaimedAfterBankruptcy,
            }
          : {
              claimed: false,
              amount: null,
              starting_date: null,
              interest_rate: null,
              basis: null,
              claimed_after_bankruptcy: null,
            },
        // Block 2.2 – priority claim (ClaimedBasis per api-spec.yaml)
        priority: priorityClaimed
          ? { claimed: true, basis: priorityBasis }
          : { claimed: false, basis: null },
        // not_conditional_amount (ClaimedBasis per api-spec.yaml;
        // blocks 2.2 and 2.3)
        not_conditional_amount: notConditionalClaimed
          ? { claimed: true, basis: notConditionalBasis }
          : { claimed: false, basis: null },
        additional_info: additionalInfo,
      };
      // material_delivery_email (Types 2.3, 2.4 – block 2.3); required,
      // validated before the confirm step.
      if (creditorType === "2.3" || creditorType === "2.4") {
        creditor.material_delivery_email = materialDeliveryEmail.trim();
      }
      // participate_decision_making (2.2–2.4 – blocks 2.2 and 2.3); 2.1
      // keeps the server value.
      if (creditorType === "2.2" || creditorType === "2.3" || creditorType === "2.4") {
        creditor.participate_decision_making = participateDecisionMaking;
      }
      // Per api-spec.yaml, `claims` only applies to types "", 2.2 and 2.4.
      // For other types strip any claims left over from the GET response,
      // since PUT /creditor/{id} replaces the whole entity.
      if (creditorType === "" || creditorType === "2.2" || creditorType === "2.4") {
        creditor.claims = backendClaims;
      } else {
        delete creditor.claims;
      }
      // Read-only fields – ignored by the server, stripped from the request.
      delete creditor.approved;
      delete creditor.approved_at;

      const updated = await updateCreditor(creditorId, creditor);

      // Refresh fields with server-confirmed data
      setOriginalCreditor(updated);
      setCreditorType(normalizeCreditorType(updated.type));
      setName(updated.name);
      setCustomerEmail(updated.email);
      setNoEmail(updated.no_email ?? false);
      setNoPhone(updated.no_phone ?? false);
      setPhone(updated.phone ?? "");
      setBankAccount(updated.bank?.bank_account ?? "");
      setBankHolderName(updated.bank?.bank_account_holder_name ?? "");
      setMaterialDeliveryEmail(updated.material_delivery_email ?? "");
      setAdditionalInfo(updated.additional_info ?? "");
      // Block 2.2 fields
      const pi = updated.penalty_interest;
      setPiClaimed(pi?.claimed ?? false);
      setPiAmount(toFinnishFloat(pi?.amount));
      setPiStartingDate(toFinnishDate(pi?.starting_date));
      setPiInterestRate(parseInterestRate(pi?.interest_rate) ?? DEFAULT_PI_INTEREST_RATE);
      setPiBasis(pi?.basis ?? "");
      setPiClaimedAfterBankruptcy(pi?.claimed_after_bankruptcy ?? false);
      setPriorityClaimed(updated.priority?.claimed ?? false);
      setPriorityBasis(updated.priority?.basis ?? "");
      setNotConditionalClaimed(
        updated.not_conditional_amount?.claimed ?? false,
      );
      setNotConditionalBasis(updated.not_conditional_amount?.basis ?? "");
      setParticipateDecisionMaking(
        updated.participate_decision_making ?? false,
      );
      setStep("success");
    } catch (err) {
      if (err instanceof Error && "status" in err) {
        const httpErr = err as { status: number };
        if (httpErr.status === 409) {
          setSaveError("errors.alreadyApproved");
        } else {
          setSaveError("errors.saveFailed");
        }
      } else {
        setSaveError("errors.connection");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = () => {
    setStep("auth");
    setAuthCode("");
    setAuthError("");
    setCreditorId("");
    setOriginalCreditor(null);
    setName("");
    setCustomerEmail("");
    setNoEmail(false);
    setNoPhone(false);
    setPhone("");
    setBankAccount("");
    setBankHolderName("");
    setAdditionalInfo("");
    setSaveError("");
    setCreditorType("");
    setClaims({});
    setFieldErrors({});
    setPiClaimed(false);
    setPiAmount("");
    setPiStartingDate("");
    setPiInterestRate(null);
    setPiBasis("");
    setPiClaimedAfterBankruptcy(false);
    setPriorityClaimed(false);
    setPriorityBasis("");
    setNotConditionalClaimed(false);
    setNotConditionalBasis("");
    setParticipateDecisionMaking(false);
    setMaterialDeliveryEmail("");
  };

  // Claims block ("Saatavani pääoman määrä 10.2.2026 (€)" + MT numbers).
  // Rendered in its previous position for creditor types other than 2.2,
  // and inside block 2.2 when 2.2 is selected.
  const claimsSection = (
    <div>
      <span className="block text-sm font-medium text-gray-700 mb-1.5">
        {t("claims.label", { date: KONKURSSIPAIVA })}
      </span>
      <div className="space-y-2">
        {Object.entries(claims).map(([claimId, amount]) => {
          const id = `claim:${claimId}`;
          return (
            <div key={claimId}>
              <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl">
                <span className="text-sm font-medium text-gray-700 font-mono flex-shrink-0">{claimId}</span>
                <div className="relative flex-1">
                  <input
                    id={id}
                    type="text"
                    value={amount}
                    onChange={(e) => {
                      const val = e.target.value;
                      // Allow empty, digits, and comma (validated on blur/save)
                      // Block input that would make it impossible to be valid
                      if (/^[0-9,]*$/.test(val)) {
                        setClaims(prev => ({ ...prev, [claimId]: val }));
                        setFieldError(id, "");
                      }
                    }}
                    onBlur={() => {
                      const error = validateOptional(amount, validateFinnishAmount);
                      setFieldError(id, error);
                      // Normalize valid amounts (e.g. "0357,90" → "357,9")
                      if (amount && !error) {
                        setClaims(prev => ({ ...prev, [claimId]: toFinnishAmount(amount.replace(",", ".")) }));
                      }
                    }}
                    {...ariaFieldError(id)}
                    className={`w-full px-3 py-1.5 bg-white border rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 text-right pr-6 disabled:opacity-50 disabled:cursor-not-allowed ${inputBorderClass(Boolean(fieldErrors[id]))}`}
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs font-medium">€</span>
                </div>
              </div>
              <FieldError id={id} message={fieldErrors[id]} />
            </div>
          );
        })}
        {Object.keys(claims).length === 0 && (
          <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl">
            <span className="text-sm text-gray-400">{t("claims.none")}</span>
          </div>
        )}
      </div>
    </div>
  );

  // Up/down buttons for the penalty interest rate: step 0,1, clamped at
  // 0; an empty field resets to the default (9,50 %) on the first click.
  const stepPiInterestRate = (dir: 1 | -1) => {
    setFieldError("piInterestRate", "");
    if (piInterestRate === null) {
      setPiInterestRate(DEFAULT_PI_INTEREST_RATE);
      return;
    }
    setPiInterestRate(roundInterestRate(piInterestRate + dir * 0.1));
  };

  // Penalty interest section ("Vaadin viivästyskorkoa" checkbox +
  // penalty_interest fields per api-spec.yaml; blocks 2.2 and 2.4).
  // Fields are shown only when the checkbox is checked. The
  // "Päivämäärä, josta alkaen viivästyskorkoa on alkanut kertymään"
  // (starting_date) input shares one grid row with the amount field
  // (`grid gap-3 lg:grid-cols-2` – side by side on lg+, stacked on
  // smaller screens) for types 2.2 and 2.4 whenever "Vaadin
  // viivästyskorkoa" is checked – independent of the etuoikeutta
  // checkbox. It accepts/displays Finnish date format (e.g. 6.12.2026).
  const penaltyInterestSection = (
    <div className="space-y-3">
      <label className="flex items-center gap-2 cursor-pointer group">
        <input
          type="checkbox"
          checked={piClaimed}
          onChange={(e) => setPiClaimed(e.target.checked)}
          className="w-4 h-4 text-blue-600 bg-gray-50 border border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
        />
        <span className="text-sm font-medium text-gray-900 group-hover:text-gray-800 transition-colors">
          {t("penaltyInterest.claimed")}
        </span>
      </label>
      {piClaimed && (
        <div className="space-y-3 pl-1">
          {/* Amount + starting date on one row (lg+): each input takes
              half the width for a clearer, more compact layout; they
              stack on smaller screens. The starting_date
              (penalty_interest.starting_date) is shown for types 2.2 and
              2.4 whenever "Vaadin viivästyskorkoa" is checked –
              independent of the etuoikeutta checkbox – and displays
              Finnish date format (e.g. 6.12.2026). */}
          <div className="grid gap-3 lg:grid-cols-2">
            <div>
              <label htmlFor="piAmount" className="block text-sm font-medium text-gray-700 mb-1.5">
                {t("penaltyInterest.amount", { date: KONKURSSIPAIVA })}
              </label>
              <div className="relative">
                <input
                  id="piAmount"
                  type="text"
                  value={piAmount}
                  onChange={(e) => {
                    const val = e.target.value;
                    // Allow empty, digits, and comma (validated on blur/save)
                    if (/^[0-9,]*$/.test(val)) {
                      setPiAmount(val);
                      setFieldError("piAmount", "");
                    }
                  }}
                  onBlur={() => {
                    const error = validateOptional(piAmount, validateFinnishAmount);
                    setFieldError("piAmount", error);
                    if (piAmount && !error) {
                      setPiAmount(toFinnishFloat(piAmount));
                    }
                  }}
                  {...ariaFieldError("piAmount")}
                  className={`w-full px-3 py-3 bg-gray-50 border rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 text-right pr-6 disabled:opacity-50 disabled:cursor-not-allowed ${inputBorderClass(Boolean(fieldErrors.piAmount))}`}
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs font-medium">€</span>
              </div>
              <FieldError id="piAmount" message={fieldErrors.piAmount} />
            </div>

            <div>
              <label htmlFor="piStartingDate" className="block text-sm font-medium text-gray-700 mb-1.5">
                {t("penaltyInterest.startingDate")}
              </label>
              <input
                id="piStartingDate"
                type="text"
                inputMode="numeric"
                lang="fi"
                maxLength={10}
                placeholder={t("penaltyInterest.startingDatePlaceholder")}
                value={piStartingDate}
                onChange={(e) => {
                  const val = e.target.value;
                  // Allow empty, digits, and dots (validated on blur/save)
                  if (/^[0-9.]*$/.test(val)) {
                    setPiStartingDate(val);
                    setFieldError("piStartingDate", "");
                  }
                }}
                onBlur={() => {
                  const error = validateOptional(piStartingDate, validateFinnishDate);
                  setFieldError("piStartingDate", error);
                  // Normalize to canonical Finnish format ("06.12.2026" → "6.12.2026")
                  if (piStartingDate && !error) {
                    setPiStartingDate(toFinnishDate(toIsoDate(piStartingDate)));
                  }
                }}
                {...ariaFieldError("piStartingDate")}
                className={`w-full px-4 py-3 bg-gray-50 border rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${inputBorderClass(Boolean(fieldErrors.piStartingDate))}`}
              />
              <FieldError id="piStartingDate" message={fieldErrors.piStartingDate} />
            </div>
          </div>

          <div className="grid gap-3 lg:grid-cols-2">
            <div>
              <label htmlFor="piInterestRate" className="block text-sm font-medium text-gray-700 mb-1.5">
                {t("penaltyInterest.rate")}
              </label>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <input
                  id="piInterestRate"
                  type="number"
                  inputMode="decimal"
                  step={0.1}
                  min={0}
                  lang="fi"
                  value={piInterestRate ?? ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFieldError("piInterestRate", "");
                    if (val === "") {
                      setPiInterestRate(null);
                      return;
                    }
                    const n = Number(val);
                    if (Number.isFinite(n)) setPiInterestRate(n);
                  }}
                  onBlur={() => {
                    const error = validateOptional(piInterestRateText, validateInterestRate);
                    setFieldError("piInterestRate", error);
                    if (piInterestRate !== null && !error) {
                      setPiInterestRate(roundInterestRate(piInterestRate));
                    }
                  }}
                  {...ariaFieldError("piInterestRate")}
                  className={`no-spin w-full px-4 py-3 pr-10 bg-gray-50 border rounded-xl text-sm text-gray-900 text-right focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${inputBorderClass(Boolean(fieldErrors.piInterestRate))}`}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs font-medium pointer-events-none">
                  %
                </span>
              </div>
              {/* Up/down increment buttons – step 0,1 (default 9,50 %) */}
              <div className="flex flex-col shrink-0">
                <button
                  type="button"
                  aria-label={t("penaltyInterest.rateUp")}
                  onClick={() => stepPiInterestRate(1)}
                  className="px-2.5 py-1 bg-gray-50 border border-b-0 border-gray-200 rounded-t-lg text-[10px] leading-none text-gray-500 hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  ▲
                </button>
                <button
                  type="button"
                  aria-label={t("penaltyInterest.rateDown")}
                  onClick={() => stepPiInterestRate(-1)}
                  className="px-2.5 py-1 bg-gray-50 border border-gray-200 rounded-b-lg text-[10px] leading-none text-gray-500 hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  ▼
                </button>
              </div>
            </div>
            <FieldError id="piInterestRate" message={fieldErrors.piInterestRate} />
          </div>
          <div>
            <label htmlFor="piBasis" className="block text-sm font-medium text-gray-700 mb-1.5">
              {t("penaltyInterest.basis")}
            </label>
            <input
              id="piBasis"
              type="text"
              value={piBasis}
              onChange={(e) => setPiBasis(e.target.value)}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={piClaimedAfterBankruptcy}
              onChange={(e) => setPiClaimedAfterBankruptcy(e.target.checked)}
              className="w-4 h-4 text-blue-600 bg-gray-50 border border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
            />
            <span className="text-sm text-gray-600 group-hover:text-gray-800 transition-colors">
              {t("penaltyInterest.afterBankruptcy")}
            </span>
          </label>
        </div>
      )}
    </div>
  );

  // material_delivery_email input (Types 2.3, 2.4 – blocks 2.3 and 2.4).
  // A text input (not type="email") so only our validation applies.
  const materialDeliveryEmailSection = (
    <div>
      <label htmlFor="materialDeliveryEmail" className="block text-sm font-medium text-gray-700 mb-1.5">
        {t("materialDeliveryEmail.label")}
      </label>
      <input
        id="materialDeliveryEmail"
        type="text"
        inputMode="email"
        value={materialDeliveryEmail}
        onChange={(e) => {
          setMaterialDeliveryEmail(e.target.value);
          setFieldError("materialDeliveryEmail", "");
        }}
        onBlur={() => {
          const error = validateOptional(materialDeliveryEmail, validateEmail);
          setFieldError("materialDeliveryEmail", error);
          if (!error) setMaterialDeliveryEmail(materialDeliveryEmail.trim());
        }}
        {...ariaFieldError("materialDeliveryEmail")}
        className={`w-full px-4 py-3 bg-gray-50 border rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${inputBorderClass(Boolean(fieldErrors.materialDeliveryEmail))}`}
      />
      <FieldError id="materialDeliveryEmail" message={fieldErrors.materialDeliveryEmail} />
    </div>
  );

  // not_conditional_amount checkbox + basis input (ClaimedBasis per
  // api-spec.yaml; blocks 2.2, 2.3 and 2.4). Checking the box activates
  // the input below the text.
  const notConditionalSection = (
    <div>
      <label className="flex items-start gap-2 cursor-pointer group">
        <input
          type="checkbox"
          checked={notConditionalClaimed}
          onChange={(e) => setNotConditionalClaimed(e.target.checked)}
          className="mt-0.5 w-4 h-4 text-blue-600 bg-gray-50 border border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
        />
        <span className="text-sm font-medium text-gray-900 group-hover:text-gray-800 transition-colors">
          {t("notConditional.label")}
        </span>
      </label>
      <input
        type="text"
        value={notConditionalBasis}
        onChange={(e) => setNotConditionalBasis(e.target.value)}
        disabled={!notConditionalClaimed}
        placeholder={notConditionalClaimed ? "" : t("notConditional.placeholder")}
        className="mt-2 w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
      />
    </div>
  );

  // Bank details for the possible distribution share (Bank per
  // api-spec.yaml; blocks 2.2, 2.3 and 2.4). The IBAN input shares state with
  // the standalone Pankkitili field.
  const bankDetailsSection = (
    <div>
      <p className="text-sm font-medium text-gray-700 mb-2">
        {t("bank.title")}
      </p>
      <div className="grid gap-3 lg:grid-cols-2">
        <div>
          <label htmlFor="bankHolderName" className="block text-sm font-medium text-gray-700 mb-1.5">
            {t("bank.holderName")}:
          </label>
          <input
            id="bankHolderName"
            type="text"
            value={bankHolderName}
            onChange={(e) => {
              setBankHolderName(e.target.value);
              setFieldError("bankHolderName", "");
            }}
            {...ariaFieldError("bankHolderName")}
            className={`w-full px-4 py-3 bg-gray-50 border rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${inputBorderClass(Boolean(fieldErrors.bankHolderName))}`}
          />
          <FieldError id="bankHolderName" message={fieldErrors.bankHolderName} />
        </div>
        <div>
          <label htmlFor="blockBankAccount" className="block text-sm font-medium text-gray-700 mb-1.5">
            {t("bank.iban")}:
          </label>
          <input
            id="blockBankAccount"
            type="text"
            value={bankAccount}
            onChange={(e) => {
              setBankAccount(e.target.value);
              setFieldError("blockBankAccount", "");
            }}
            onBlur={() => {
              const error = validateOptional(bankAccount, validateIban);
              setFieldError("blockBankAccount", error);
              // Normalize to groups of four ("FI21 1234 5600 0007 85")
              if (bankAccount.trim() && !error) {
                setBankAccount(formatIban(bankAccount));
              }
            }}
            placeholder="FI00 0000 0000 0000 00"
            {...ariaFieldError("blockBankAccount")}
            className={`w-full px-4 py-3 bg-gray-50 border rounded-xl text-gray-900 placeholder-gray-400 font-mono tracking-wider focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${inputBorderClass(Boolean(fieldErrors.blockBankAccount))}`}
          />
          <FieldError id="blockBankAccount" message={fieldErrors.blockBankAccount} />
        </div>
      </div>
    </div>
  );

  // Priority claim section ("Vaadin saatavalleni etuoikeutta" checkbox;
  // priority per api-spec.yaml; blocks 2.2 and 2.4). Checking the box
  // shows the priority basis input. The not_conditional_amount checkbox
  // + basis input below it is always visible, independent of the
  // etuoikeutta checkbox (input activates only when checked).
  const prioritySection = (
    <div className="space-y-3">
      <label className="flex items-center gap-2 cursor-pointer group">
        <input
          type="checkbox"
          checked={priorityClaimed}
          onChange={(e) => setPriorityClaimed(e.target.checked)}
          className="w-4 h-4 text-blue-600 bg-gray-50 border border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
        />
        <span className="text-sm font-medium text-gray-900 group-hover:text-gray-800 transition-colors">
          {t("priority.claimed")}
        </span>
      </label>

      {priorityClaimed && (
        <div className="pl-1">
          <div>
            <label htmlFor="priorityBasis" className="block text-sm font-medium text-gray-700 mb-1.5">
              {t("priority.basisLabel")}
            </label>
            <input
              id="priorityBasis"
              type="text"
              value={priorityBasis}
              onChange={(e) => setPriorityBasis(e.target.value)}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        </div>
      )}

      {/* not_conditional_amount (ClaimedBasis per api-spec.yaml) – always
          visible, also when etuoikeutta is not claimed */}
      {notConditionalSection}
    </div>
  );

  // "Mikäli" info box shown at the top of blocks 2.2, 2.3 and 2.4.
  // Option 2.1 needs no further information, so it is not shown there.
  const correctionNotice = (
    <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
      <div className="flex items-start gap-2.5">
        <svg className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div className="text-sm text-blue-800 space-y-2">
          <p>{t("correctionNotice.intro")}</p>
          <ul className="space-y-1 pl-4">
            {t("correctionNotice.items", { returnObjects: true }).map((item, i) => (
              <li key={i} className="flex gap-2">
                <span className="font-semibold flex-shrink-0">{["I)", "II)"][i]}</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p>{t("correctionNotice.outro")}</p>
        </div>
      </div>
    </div>
  );

  // participate_decision_making radio group (blocks 2.2, 2.3 and 2.4).
  // "Ilmoitan, että en osallistu" = false (value 0, listed first and
  // preselected), "Ilmoitan osallistuvani" = true (value 1).
  const participateDecisionSection = (
    <div>
      <h3 className="text-sm font-semibold text-gray-900">
        {t("participate.title")}
      </h3>
      <p className="text-sm text-gray-600 mt-1.5 leading-relaxed">
        {t("participate.info")}
      </p>
      <div className="mt-2 grid gap-2 lg:grid-cols-2">
        {PARTICIPATE_OPTIONS.map((opt) => (
          <label
            key={String(opt.value)}
            className={`flex items-center gap-3 px-4 py-3 border rounded-xl cursor-pointer transition-all duration-200 ${
              participateDecisionMaking === opt.value
                ? "bg-blue-50 border-blue-300 ring-1 ring-blue-200"
                : "bg-gray-50 border-gray-200 hover:border-blue-200 hover:bg-blue-50/50"
            }`}
          >
            <input
              type="radio"
              name="participateDecisionMaking"
              checked={participateDecisionMaking === opt.value}
              onChange={() => setParticipateDecisionMaking(opt.value)}
              className="mt-0.5 w-4 h-4 text-blue-600 bg-white border-gray-300 rounded-full focus:ring-blue-500 flex-shrink-0"
            />
            <span className="text-sm text-gray-700">{t(opt.labelKey)}</span>
          </label>
        ))}
      </div>
    </div>
  );

  // Summary of the submitted data (confirm and success steps). Shows the
  // same type-specific blocks as the edit form, using the same conditions
  // as validateVisibleFields() and handleSubmit().
  const hasClaims = creditorType === "2.2" || creditorType === "2.4";
  const hasMaterialEmail = creditorType === "2.3" || creditorType === "2.4";
  const hasBank =
    creditorType === "2.2" || creditorType === "2.3" || creditorType === "2.4";

  // Finnish amount ("357,9") or dot-decimal ("357.9") → "357,90 €"
  const formatEuro = (amount: string) =>
    `${toBackendAmount(amount).replace(".", ",")} €`;
  const claimsTotalCents = Object.values(claims).reduce(
    (sum, amount) => sum + Math.round(Number(toBackendAmount(amount)) * 100),
    0,
  );

  // One row: label above value, so long labels and free text wrap
  // cleanly. `wide` spans both grid columns.
  const summaryRow = (label: string, value: React.ReactNode, wide = false) => (
    <div className={wide ? "lg:col-span-2" : ""}>
      <dt className="text-xs font-medium text-gray-500">{label}</dt>
      <dd className="mt-0.5 text-sm text-gray-700 whitespace-pre-wrap break-words">
        {value === "" ? <span className="text-gray-400">–</span> : value}
      </dd>
    </div>
  );

  const summaryGroup = (title: string, rows: React.ReactNode) => (
    <div className="space-y-1">
      <h4 className="text-sm font-semibold text-gray-900">
        {title}
      </h4>
      <dl className="grid gap-x-6 gap-y-3 lg:grid-cols-2 bg-white border border-gray-200 rounded-lg px-3 py-2.5">{rows}</dl>
    </div>
  );

  // A group with a single value and no row label: the header says it all
  const summaryText = (title: string, text: string) => (
    <div className="space-y-1">
      <h4 className="text-sm font-semibold text-gray-900">
        {title}
      </h4>
      <p className="text-sm text-gray-700 whitespace-pre-wrap break-words bg-white border border-gray-200 rounded-lg px-3 py-2.5">
        {text || <span className="text-gray-400">–</span>}
      </p>
    </div>
  );

  const selectedTypeOption = CREDITOR_TYPE_OPTIONS.find(
    (o) => o.value === creditorType,
  );

  const summarySection = (
    <div className="bg-gray-50 rounded-xl p-4 text-left border border-gray-100 divide-y divide-gray-200">
      <section className="pb-4 space-y-3">
        <h3 className="text-base font-semibold text-gray-900">{t("creditor.title")}</h3>
        <dl className="grid gap-x-6 gap-y-3 lg:grid-cols-3 bg-white border border-gray-200 rounded-lg px-3 py-2.5">
          {summaryRow(t("creditor.name"), name)}
          {summaryRow(t("creditor.email"), noEmail ? t("creditor.noEmailValue") : customerEmail)}
          {summaryRow(t("creditor.phone"), noPhone ? t("creditor.noPhoneValue") : phone)}
        </dl>
      </section>

      <section className="py-4 last:pb-0 space-y-6">
        <h3 className="text-base font-semibold text-gray-900">
          {t("creditorType.title")}
        </h3>

        <p className="px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 leading-snug">
          {selectedTypeOption ? (
            <>
              <span className="font-semibold text-gray-900">{selectedTypeOption.value}.</span>{" "}
              {t(selectedTypeOption.labelKey)}
            </>
          ) : (
            t("creditorType.notSelected")
          )}
        </p>

        {hasMaterialEmail &&
          summaryText(
            t("materialDeliveryEmail.label"),
            materialDeliveryEmail,
          )}

        {hasClaims &&
          summaryGroup(
            t("claims.summaryTitle", { date: KONKURSSIPAIVA }),
            <div className="lg:col-span-2">
              {Object.keys(claims).length === 0 ? (
                <p className="text-sm text-gray-400">{t("claims.none")}</p>
              ) : (
                <ul className="-my-2 divide-y divide-gray-200 text-sm">
                  {Object.entries(claims).map(([claimId, amount]) => (
                    <li key={claimId} className="flex justify-between gap-4 py-2">
                      <span className="font-mono text-gray-600">{claimId}</span>
                      <span className="font-medium text-gray-900 tabular-nums">{formatEuro(amount)}</span>
                    </li>
                  ))}
                  {Object.keys(claims).length > 1 && (
                    <li className="flex justify-between gap-4 py-2 font-semibold text-gray-900">
                      <span>{t("claims.total")}</span>
                      <span className="tabular-nums">{formatEuro(String(claimsTotalCents / 100))}</span>
                    </li>
                  )}
                </ul>
              )}
            </div>,
          )}

        {hasClaims &&
          (piClaimed
            ? summaryGroup(
                t("penaltyInterest.claimed"),
                <>
                  {summaryRow(t("penaltyInterest.summaryAmount", { date: KONKURSSIPAIVA }), piAmount && formatEuro(piAmount))}
                  {summaryRow(t("penaltyInterest.startingDate"), piStartingDate)}
                  {summaryRow(
                    t("penaltyInterest.rate"),
                    piInterestRate === null ? "" : `${piInterestRate.toFixed(2).replace(".", ",")} %`,
                  )}
                  {summaryRow(t("penaltyInterest.basis"), piBasis)}
                  {summaryRow(
                    t("penaltyInterest.afterBankruptcy"),
                    piClaimedAfterBankruptcy
                      ? t("common.yes")
                      : "-",
                    true,
                  )}
                </>,
              )
            : summaryText(t("penaltyInterest.claimed"), ""))}

        {hasClaims &&
          (priorityClaimed
            ? summaryGroup(
                t("priority.claimed"),
                summaryRow(t("priority.summaryBasis"), priorityBasis, true),
              )
            : summaryText(
                t("priority.claimed"),
                "",
              ))}

        {hasBank &&
          (notConditionalClaimed
            ? summaryGroup(
                t("notConditional.title"),
                summaryRow(
                  t("notConditional.label"),
                  notConditionalBasis,
                  true,
                ),
              )
            : summaryText(
                t("notConditional.title"),
                "",
              ))}

        {hasBank &&
          summaryGroup(
            t("bank.title"),
            <>
              {summaryRow(t("bank.holderName"), bankHolderName)}
              {summaryRow(
                t("bank.iban"),
                bankAccount && <span className="font-mono">{formatIban(bankAccount)}</span>,
              )}
            </>,
          )}

        {hasBank &&
          summaryText(
            t("participate.title"),
            t(PARTICIPATE_OPTIONS.find((o) => o.value === participateDecisionMaking)!.labelKey),
          )}
      </section>

      {additionalInfo.trim() && (
        <section className="pt-4">
          {summaryText(t("additionalInfo.label"), additionalInfo)}
        </section>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f0f2f5] flex items-center justify-center px-4 py-8">
      {/* Login stays narrow (auth code has limited length); other steps widen on desktop */}
      <div className={`w-full ${step === "auth" ? "max-w-md" : "max-w-md lg:max-w-4xl xl:max-w-5xl"}`}>
        {/* Language switcher (above the header, right-aligned) */}
        <LanguageSwitcher />

        {/* Header */}
        <div className="text-center mb-8">
          <img
            src={logoImg}
            alt="Eversheds Sutherland"
            className="mx-auto mb-4 h-24 w-auto sm:h-28 shadow-md"
            draggable={false}
          />
          <h1 className="text-2xl font-bold text-gray-900">Indoor Group Oy</h1>
          <p className="text-gray-500 text-sm mt-1">{t("header.subtitle")}</p>
        </div>

        {/* Card Container */}
        <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/60 border border-gray-100 overflow-hidden">
          
          {/* Step Indicator */}
          {(step === "edit" || step === "confirm") && (
            <div className="bg-blue-50 border-b border-blue-100 px-6 py-3 flex items-center gap-2">
              <div className="flex items-center gap-1.5">
                <span className="flex items-center justify-center w-6 h-6 bg-blue-600 text-white text-xs font-semibold rounded-full">1</span>
                <span className="text-sm font-medium text-blue-800">{t("steps.edit")}</span>
              </div>
              <div className="w-6 h-px bg-blue-300" />
              <div className="flex items-center gap-1.5">
                <span className={`flex items-center justify-center w-6 h-6 text-xs font-semibold rounded-full ${step === "confirm" ? "bg-blue-600 text-white" : "bg-blue-200 text-blue-600"}`}>2</span>
                <span className={`text-sm font-medium ${step === "confirm" ? "text-blue-800" : "text-blue-400"}`}>{t("steps.confirm")}</span>
              </div>
            </div>
          )}

          <div className="p-6 sm:p-8">
            
            {/* STEP 1: Authentication */}
            {step === "auth" && (
              <form onSubmit={handleAuth} className="space-y-5">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-1">
                    {t("auth.title")}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {t("auth.subtitle")}
                  </p>
                </div>

                <div>
                  <label htmlFor="authCode" className="block text-sm font-medium text-gray-700 mb-1.5">
                    {t("auth.codeLabel")}
                  </label>
                  <input
                    id="authCode"
                    type="text"
                    required
                    value={authCode}
                    onChange={(e) => setAuthCode(e.target.value)}
                    placeholder="xxxx-xxxx-xxxx"
                    disabled={isLoading}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 font-mono tracking-wider uppercase disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>

                {authError && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
                    <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm text-red-700">{t(authError as ParseKeys)}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-10 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-all duration-200 shadow-lg shadow-blue-600/25 hover:shadow-blue-600/40 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      {t("common.loggingIn")}
                    </>
                  ) : (
                    t("common.continue")
                  )}
                </button>
              </form>
            )}

            {/* STEP 2: Edit Customer Data (preview before confirm) */}
            {step === "edit" && (
              <form onSubmit={(e) => {
                e.preventDefault();
                // Show all field errors at once and focus the first invalid field
                const errors = validateVisibleFields();
                setFieldErrors(errors);
                const firstInvalid = Object.keys(errors).find((id) => errors[id]);
                if (firstInvalid) {
                  // Radio groups are keyed by their name: focus the first option
                  (
                    document.getElementById(firstInvalid) ??
                    document.querySelector<HTMLElement>(`[name="${firstInvalid}"]`)
                  )?.focus();
                  return;
                }
                setStep("confirm");
              }} noValidate className="space-y-5">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-1">
                    {t("creditor.title")}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {t("edit.subtitle")}
                  </p>
                </div>

                {/* Contract terms block (Step 2: Muokkaa tietoja) */}
                <ContractTerms />

                <div className="space-y-4">
                  {/* Personal details – side by side on wide screens */}
                  <div className="grid gap-4 lg:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">
                      {t("creditor.name")}
                    </label>
                    <input
                      id="name"
                      type="text"
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        setFieldError("name", "");
                      }}
                      {...ariaFieldError("name")}
                      className={`w-full px-4 py-3 bg-gray-50 border rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${inputBorderClass(Boolean(fieldErrors.name))}`}
                    />
                    <FieldError id="name" message={fieldErrors.name} />
                  </div>

                  <div>
                    <label htmlFor="customerEmail" className="block text-sm font-medium text-gray-700 mb-1.5">
                      {t("creditor.email")}
                    </label>
                    <input
                      id="customerEmail"
                      type="text"
                      inputMode="email"
                      value={noEmail ? "" : customerEmail}
                      onChange={(e) => {
                        setCustomerEmail(e.target.value);
                        setFieldError("customerEmail", "");
                      }}
                      onBlur={() => {
                        const error = validateOptional(customerEmail, validateEmail);
                        setFieldError("customerEmail", error);
                        if (!error) setCustomerEmail(customerEmail.trim());
                      }}
                      disabled={noEmail}
                      placeholder={noEmail ? t("creditor.noEmailValue") : ""}
                      {...ariaFieldError("customerEmail")}
                      className={`w-full px-4 py-3 bg-gray-50 border rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${inputBorderClass(Boolean(fieldErrors.customerEmail))}`}
                    />
                    <FieldError id="customerEmail" message={fieldErrors.customerEmail} />
                    <label className="flex items-center gap-2 mt-2 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={noEmail}
                        onChange={(e) => {
                          setNoEmail(e.target.checked);
                          setFieldError("noPhone", "");
                          if (e.target.checked) {
                            setCustomerEmail("");
                            setFieldError("customerEmail", "");
                          }
                        }}
                        className="w-4 h-4 text-blue-600 bg-gray-50 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                      />
                      <span className="text-sm text-gray-600 group-hover:text-gray-800 transition-colors">
                        {t("creditor.noEmail")}
                      </span>
                    </label>
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1.5">
                      {t("creditor.phone")}
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      value={noPhone ? "" : phone}
                      onChange={(e) => {
                        setPhone(e.target.value);
                        setFieldError("phone", "");
                      }}
                      onBlur={() => {
                        const error = validateOptional(phone, validatePhone);
                        setFieldError("phone", error);
                        if (!error) setPhone(phone.trim());
                      }}
                      placeholder={noPhone ? t("creditor.noPhoneValue") : t("creditor.phonePlaceholder")}
                      disabled={noPhone}
                      {...ariaFieldError("phone")}
                      className={`w-full px-4 py-3 bg-gray-50 border rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${inputBorderClass(Boolean(fieldErrors.phone))}`}
                    />
                    <FieldError id="phone" message={fieldErrors.phone} />
                    <label className="flex items-center gap-2 mt-2 cursor-pointer group">
                      <input
                        id="noPhone"
                        type="checkbox"
                        checked={noPhone}
                        onChange={(e) => {
                          setNoPhone(e.target.checked);
                          setFieldError("noPhone", "");
                          if (e.target.checked) {
                            setPhone("");
                            setFieldError("phone", "");
                          }
                        }}
                        {...ariaFieldError("noPhone")}
                        className="w-4 h-4 text-blue-600 bg-gray-50 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                      />
                      <span className="text-sm text-gray-600 group-hover:text-gray-800 transition-colors">
                        {t("creditor.noPhone")}
                      </span>
                    </label>
                    <FieldError id="noPhone" message={fieldErrors.noPhone} />
                  </div>

                  </div>

                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 mb-1">
                      {t("creditorType.title")}
                    </h2>

                    {/* Creditor:type – select exactly one option (2.1–2.4);
                        nothing is selected until the customer chooses */}
                    <div className="mt-3">
                      <span className="block text-sm font-medium text-gray-700 mb-2">
                        {t("creditorType.prompt")}
                      </span>
                      <div className="space-y-2">
                        {CREDITOR_TYPE_OPTIONS.map((option) => {
                          const isChecked = creditorType === option.value;
                          return (
                            <div key={option.value}>
                              <label
                                className={`flex items-start gap-3 px-4 py-3 border rounded-xl cursor-pointer transition-all duration-200 ${
                                  isChecked
                                    ? "bg-blue-50 border-blue-300 ring-1 ring-blue-200"
                                    : "bg-gray-50 border-gray-200 hover:border-blue-200 hover:bg-blue-50/50"
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="creditorType"
                                  checked={isChecked}
                                  onChange={() => {
                                    // Creditor:type is a single enum value
                                    // in api-spec.yaml
                                    setCreditorType(option.value);
                                    setFieldError("creditorType", "");
                                  }}
                                  {...ariaFieldError("creditorType")}
                                  className="mt-0.5 w-4 h-4 text-blue-600 bg-white border-gray-300 rounded-full focus:ring-blue-500 flex-shrink-0"
                                />
                                <span className="text-sm text-gray-700 leading-snug">
                                  <span className="font-semibold text-gray-900">{option.value}.</span>{" "}
                                  {t(option.labelKey)}
                                </span>
                              </label>

                              {/* Shown under block 2.2 when Creditor:type 2.2 is selected */}
                              {option.value === "2.2" && isChecked && (
                                <div className="mt-2 ml-4 p-4 bg-white border border-blue-200 rounded-xl shadow-sm space-y-5">
                                  {/* "Mikäli" info box (shared const – blocks 2.2, 2.3 and 2.4) */}
                                  {correctionNotice}

                                  {/* Claims moved into block 2.2 */}
                                  {claimsSection}

                                  {/* Penalty interest (shared const – blocks 2.2 and 2.4) */}
                                  {penaltyInterestSection}

                                  {/* Priority claim (shared const – blocks 2.2 and 2.4) */}
                                  {prioritySection}

                                  {/* Bank details for the possible distribution share */}
                                  {bankDetailsSection}

                                  {/* participate_decision_making (shown whenever
                                      option 2.2 is selected) */}
                                  {participateDecisionSection}
                                </div>
                              )}

                              {/* Shown under block 2.3 when Creditor:type 2.3 is selected */}
                              {option.value === "2.3" && isChecked && (
                                <div className="mt-2 ml-4 p-4 bg-white border border-blue-200 rounded-xl shadow-sm space-y-5">
                                  {/* "Mikäli" info box (shared const – blocks 2.2, 2.3 and 2.4) */}
                                  {correctionNotice}

                                  {/* material_delivery_email (shared const – blocks 2.3 and 2.4) */}
                                  {materialDeliveryEmailSection}

                                  {/* not_conditional_amount (ClaimedBasis per api-spec.yaml) */}
                                  {notConditionalSection}

                                  {/* Bank details for the possible distribution share */}
                                  {bankDetailsSection}

                                  {/* participate_decision_making */}
                                  {participateDecisionSection}
                                </div>
                              )}

                              {/* Shown under block 2.4 when Creditor:type 2.4 is selected */}
                              {option.value === "2.4" && isChecked && (
                                <div className="mt-2 ml-4 p-4 bg-white border border-blue-200 rounded-xl shadow-sm space-y-5">
                                  {/* "Mikäli" info box (shared const – blocks 2.2, 2.3 and 2.4) */}
                                  {correctionNotice}

                                  {/* material_delivery_email (Types 2.3, 2.4) */}
                                  {materialDeliveryEmailSection}

                                  {/* Claims – Saatavani pääoman määrä 10.2.2026 (€) + MT-numbers (alike block 2.2) */}
                                  {claimsSection}

                                  {/* Penalty interest (penalty_interest per api-spec.yaml) */}
                                  {penaltyInterestSection}

                                  {/* Priority claim (priority per api-spec.yaml) + not_conditional_amount */}
                                  {prioritySection}

                                  {/* Bank details for the possible distribution share */}
                                  {bankDetailsSection}

                                  {/* participate_decision_making */}
                                  {participateDecisionSection}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <FieldError id="creditorType" message={fieldErrors.creditorType} />
                    </div>

                    {/* Claims ("Saatavani pääoman määrä 10.2.2026 (€)" + MT
                        numbers) are only shown inside blocks 2.2 and 2.4 –
                        not under 2.1 */}
                  </div>

                  <div>
                    <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700 mb-1.5">
                      {t("additionalInfo.label")}
                    </label>
                    <textarea
                      id="additionalInfo"
                      rows={3}
                      value={additionalInfo}
                      onChange={(e) => setAdditionalInfo(e.target.value)}
                      placeholder={t("additionalInfo.placeholder")}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="px-5 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-all duration-200"
                  >
                    {t("common.back")}
                  </button>
                  <button
                    type="submit"
                    className="px-10 py-3 font-semibold rounded-xl transition-all duration-200 active:scale-[0.98] flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/25 hover:shadow-blue-600/40"
                  >
                    {t("common.continue")}
                  </button>
                </div>
              </form>
            )}

            {/* STEP 3: Confirm & Submit */}
            {step === "confirm" && (
              <div className="space-y-5">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-1">
                    {t("confirm.title")}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {t("confirm.subtitle")}
                  </p>
                </div>

                {saveError && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
                    <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm text-red-700">{t(saveError as ParseKeys)}</p>
                  </div>
                )}

                {summarySection}

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setStep("edit")}
                    disabled={isSaving}
                    className="px-5 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-all duration-200 disabled:opacity-50"
                  >
                    {t("common.back")}
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSaving}
                    className="px-10 py-3 font-semibold rounded-xl transition-all duration-200 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white shadow-lg shadow-green-600/25 hover:shadow-green-600/40"
                  >
                    {isSaving ? (
                      <>
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        {t("common.saving")}
                      </>
                    ) : (
                      t("common.confirm")
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Already approved: read-only summary instead of the edit form */}
            {step === "approved" && (
              <div className="space-y-5">
                <div className="flex items-start gap-3 p-4 bg-amber-50 border-2 border-amber-300 rounded-xl shadow-sm">
                  <svg className="w-7 h-7 text-amber-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <p className="text-sm font-semibold text-amber-800 leading-relaxed">{t("edit.alreadyApproved")}</p>
                </div>

                <ContractTerms defaultExpanded={false} />

                {summarySection}

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="px-5 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-all duration-200"
                  >
                    {t("common.logout")}
                  </button>
                </div>
              </div>
            )}

            {/* STEP 4: Success */}
            {step === "success" && (
              <div className="text-center py-6 space-y-5">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full">
                  <svg className="w-10 h-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>

                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-2">
                    {t("success.title")}
                  </h2>
                  <p className="text-gray-600">
                    {t("success.message")}
                  </p>
                </div>

                {summarySection}

                <button
                  onClick={handleLogout}
                  className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-all duration-200 shadow-lg shadow-blue-600/25"
                >
                  {t("common.logout")}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 mt-6">
          {t("footer")}
        </p>
      </div>
    </div>
  );
}

export default App;
